import { useState, useEffect, useRef } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  getTemplates,
  getCountryCodes,
  createCampaign,
  downloadTemplateCSV,
  saveTemplateSettings,
  resetTemplateSettings,
} from '@/lib/campaigns';
import type { Template, CountryCode } from '@/types/campaigns';
import {
  ArrowLeft,
  Download,
  Upload,
  FileSpreadsheet,
  AlertCircle,
  CheckCircle,
  Plus,
  X,
  Users,
  ChevronDown,
  ChevronUp,
  Save,
  RotateCcw,
} from 'lucide-react';

export function CampaignCreatePage() {
  const navigate = useNavigate();
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Form state
  const [name, setName] = useState('');
  const [templateId, setTemplateId] = useState('');
  const [channel, setChannel] = useState<'voice' | 'sms'>('voice');
  const [countryCode, setCountryCode] = useState('+972');
  const [startHour, setStartHour] = useState(9);
  const [endHour, setEndHour] = useState(18);
  const [allowedDays, setAllowedDays] = useState<string[]>([
    'sunday',
    'monday',
    'tuesday',
    'wednesday',
    'thursday',
  ]);
  const [callReason, setCallReason] = useState('');
  const [enableSurvey, setEnableSurvey] = useState(false);
  const [enableSmsFollowUp, setEnableSmsFollowUp] = useState(false);
  const [smsTemplate, setSmsTemplate] = useState('');
  const [csvFile, setCsvFile] = useState<File | null>(null);

  // Contact entry mode
  const [inputMode, setInputMode] = useState<'csv' | 'manual'>('manual');
  const [manualContacts, setManualContacts] = useState<
    Record<string, string>[]
  >([{}]);

  // Advanced settings (collapsed by default)
  const [showAdvanced, setShowAdvanced] = useState(false);

  // Data state
  const [templates, setTemplates] = useState<Template[]>([]);
  const [countryCodes, setCountryCodes] = useState<CountryCode[]>([]);

  // UI state
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [savingSettings, setSavingSettings] = useState(false);
  const [resettingSettings, setResettingSettings] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Load initial data
  useEffect(() => {
    async function loadData() {
      try {
        const [templatesData, countryCodesData] = await Promise.all([
          getTemplates(),
          getCountryCodes(),
        ]);
        setTemplates(templatesData);
        setCountryCodes(countryCodesData);
      } catch (err) {
        setError('Failed to load form data');
        console.error(err);
      } finally {
        setLoading(false);
      }
    }
    loadData();
  }, []);

  const selectedTemplate = templates.find((t) => t.id === templateId);

  const handleDownloadTemplate = async () => {
    if (!templateId) return;
    try {
      const blob = await downloadTemplateCSV(templateId);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${templateId}_template.csv`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Failed to download template:', err);
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setCsvFile(file);
    }
  };

  // Reset contacts and pre-fill form when template changes
  useEffect(() => {
    if (selectedTemplate) {
      const emptyContact: Record<string, string> = {};
      selectedTemplate.required_columns.forEach(
        (col) => (emptyContact[col] = ''),
      );
      setManualContacts([emptyContact]);
      // Pre-fill all fields from template defaults
      setChannel(selectedTemplate.default_channel);
      setStartHour(selectedTemplate.default_start_hour);
      setEndHour(selectedTemplate.default_end_hour);
      setCallReason(selectedTemplate.default_call_reason || '');
      setEnableSurvey(selectedTemplate.default_enable_survey);
      setEnableSmsFollowUp(selectedTemplate.default_enable_sms_follow_up);
      setSmsTemplate(selectedTemplate.default_sms_template || '');
    }
  }, [templateId, selectedTemplate]);

  // Manual contact helpers
  const addContact = () => {
    const emptyContact: Record<string, string> = {};
    selectedTemplate?.required_columns.forEach(
      (col) => (emptyContact[col] = ''),
    );
    setManualContacts([...manualContacts, emptyContact]);
  };

  const removeContact = (index: number) => {
    if (manualContacts.length > 1) {
      setManualContacts(manualContacts.filter((_, i) => i !== index));
    }
  };

  const updateContact = (index: number, field: string, value: string) => {
    const updated = [...manualContacts];
    updated[index] = { ...updated[index], [field]: value };
    setManualContacts(updated);
  };

  const createCSVFromContacts = (
    contacts: Record<string, string>[],
    columns: string[],
  ): File => {
    const header = columns.join(',');
    const rows = contacts
      .filter((c) => c.phone?.trim()) // Only include contacts with phone
      .map((c) => columns.map((col) => c[col]?.trim() || '').join(','));
    const csv = [header, ...rows].join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    return new File([blob], 'contacts.csv', { type: 'text/csv' });
  };

  // Format column name for display (e.g., "order_id" -> "Order ID")
  const formatColumnName = (col: string): string => {
    return col
      .split('_')
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  };

  const validManualContacts = manualContacts.filter((c) => c.phone?.trim());

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    // Validate template
    if (!templateId) {
      setError('Please select a campaign type');
      return;
    }

    // Validate call reason (required)
    if (!callReason.trim()) {
      setError('Please enter a call reason');
      return;
    }

    // Validate contacts based on input mode
    let fileToUpload: File;
    if (inputMode === 'csv') {
      if (!csvFile) {
        setError('Please upload a CSV file');
        return;
      }
      fileToUpload = csvFile;
    } else {
      if (!selectedTemplate) {
        setError('Please select a campaign type first');
        return;
      }
      if (validManualContacts.length === 0) {
        setError('Please add at least one contact with a phone number');
        return;
      }
      fileToUpload = createCSVFromContacts(
        validManualContacts,
        selectedTemplate.required_columns,
      );
    }

    try {
      setSubmitting(true);
      const result = await createCampaign(templateId, fileToUpload, {
        name: name || undefined,
        channel,
        countryCode,
        startHour,
        endHour,
        callReason,
        enableSurvey,
        enableSmsFollowUp,
        smsTemplate: smsTemplate || undefined,
        allowedDays,
      });

      if (result.success && result.campaign_id) {
        setSuccess(`Campaign created with ${result.contacts_count} contacts`);
        setTimeout(() => {
          navigate(`/campaigns/${result.campaign_id}`);
        }, 1500);
      } else {
        setError(result.errors.join(', ') || 'Failed to create campaign');
      }
    } catch (err) {
      setError('Failed to create campaign. Please try again.');
      console.error(err);
    } finally {
      setSubmitting(false);
    }
  };

  const handleSaveSettings = async () => {
    if (!templateId) return;
    setSavingSettings(true);
    setError('');
    setSuccess('');
    try {
      await saveTemplateSettings(templateId, {
        default_channel: channel,
        default_start_hour: startHour,
        default_end_hour: endHour,
        default_call_reason: callReason || undefined,
        default_enable_survey: enableSurvey,
        default_enable_sms_follow_up: enableSmsFollowUp,
        default_sms_template: smsTemplate || undefined,
      });
      // Refresh templates to get updated is_customized flag
      const updatedTemplates = await getTemplates();
      setTemplates(updatedTemplates);
      setSuccess('Settings saved for future campaigns');
    } catch (err) {
      setError('Failed to save settings');
      console.error(err);
    } finally {
      setSavingSettings(false);
    }
  };

  const handleResetSettings = async () => {
    if (!templateId) return;
    setResettingSettings(true);
    setError('');
    setSuccess('');
    try {
      await resetTemplateSettings(templateId);
      // Refresh templates to get updated defaults
      const updatedTemplates = await getTemplates();
      setTemplates(updatedTemplates);
      // Re-apply the (now system) defaults to form
      const resetTemplate = updatedTemplates.find((t) => t.id === templateId);
      if (resetTemplate) {
        setChannel(resetTemplate.default_channel);
        setStartHour(resetTemplate.default_start_hour);
        setEndHour(resetTemplate.default_end_hour);
        setCallReason(resetTemplate.default_call_reason || '');
        setEnableSurvey(resetTemplate.default_enable_survey);
        setEnableSmsFollowUp(resetTemplate.default_enable_sms_follow_up);
        setSmsTemplate(resetTemplate.default_sms_template || '');
      }
      setSuccess('Settings reset to original');
    } catch (err) {
      setError('Failed to reset settings');
      console.error(err);
    } finally {
      setResettingSettings(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="h-8 w-8 animate-spin rounded-full border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-2xl space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="icon" asChild>
          <Link to="/campaigns">
            <ArrowLeft className="h-4 w-4" />
          </Link>
        </Button>
        <div>
          <h1 className="text-3xl font-bold">New Campaign</h1>
          <p className="text-muted-foreground">
            Create a new outbound calling campaign
          </p>
        </div>
      </div>

      {/* Form */}
      <form onSubmit={handleSubmit}>
        <Card>
          <CardHeader>
            <CardTitle>Campaign Details</CardTitle>
            <CardDescription>
              Configure your campaign settings and upload contacts
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Template Selection */}
            <div className="space-y-2">
              <Label htmlFor="template">Campaign Type *</Label>
              <Select value={templateId} onValueChange={setTemplateId}>
                <SelectTrigger>
                  <SelectValue placeholder="Select a campaign type" />
                </SelectTrigger>
                <SelectContent>
                  {templates.map((template) => (
                    <SelectItem key={template.id} value={template.id}>
                      <span className="flex items-center gap-2">
                        {template.name}
                        {template.is_customized && (
                          <span className="rounded bg-blue-100 px-1.5 py-0.5 text-xs text-blue-700">
                            Customized
                          </span>
                        )}
                      </span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {selectedTemplate && (
                <p className="text-sm text-muted-foreground">
                  {selectedTemplate.description}
                </p>
              )}
            </div>

            {/* Channel Selection */}
            <div className="space-y-2">
              <Label htmlFor="channel">Channel *</Label>
              <Select
                value={channel}
                onValueChange={(v) => setChannel(v as 'voice' | 'sms')}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="voice">Voice (Phone Calls)</SelectItem>
                  <SelectItem value="sms">SMS (Text Messages)</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Campaign Name (optional) */}
            <div className="space-y-2">
              <Label htmlFor="name">Campaign Name</Label>
              <Input
                id="name"
                placeholder="Auto-generated if empty"
                value={name}
                onChange={(e) => setName(e.target.value)}
              />
              <p className="text-sm text-muted-foreground">
                Leave empty to auto-generate from campaign type and date
              </p>
            </div>

            {/* Call Reason / SMS Message (required) */}
            <div className="space-y-3">
              <div className="space-y-2">
                <Label htmlFor="callReason">
                  {channel === 'sms' ? 'Message *' : 'Call Reason *'}
                </Label>
                <Textarea
                  id="callReason"
                  value={callReason}
                  onChange={(e) => setCallReason(e.target.value)}
                  placeholder={
                    !selectedTemplate?.default_call_reason
                      ? channel === 'sms'
                        ? 'e.g., Hi {name}, your delivery is scheduled for {date}'
                        : "e.g., We're calling about {reason}"
                      : undefined
                  }
                  required
                />
                <div className="flex justify-between text-sm text-muted-foreground">
                  <span>
                    Use {'{column_name}'} for personalization from CSV data
                  </span>
                  {channel === 'sms' && (
                    <span
                      className={
                        callReason.length > 160
                          ? 'font-medium text-yellow-600'
                          : ''
                      }
                    >
                      {callReason.length}/160{' '}
                      {callReason.length > 160 && '(will be split)'}
                    </span>
                  )}
                </div>
              </div>
            </div>

            {/* SMS Follow-up (voice campaigns only) */}
            {channel === 'voice' && (
              <div className="space-y-3">
                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="enableSmsFollowUp"
                    checked={enableSmsFollowUp}
                    onCheckedChange={(checked) =>
                      setEnableSmsFollowUp(checked === true)
                    }
                  />
                  <Label htmlFor="enableSmsFollowUp" className="cursor-pointer">
                    Send SMS after call
                  </Label>
                </div>
                {enableSmsFollowUp && (
                  <div className="space-y-2 pl-6">
                    <Textarea
                      id="smsTemplate"
                      value={smsTemplate}
                      onChange={(e) => setSmsTemplate(e.target.value)}
                      placeholder="e.g., Thank you for speaking with us! Your reference: {order_id}"
                      rows={3}
                    />
                    <div className="flex justify-between text-sm text-muted-foreground">
                      <span>
                        Use {'{column_name}'} for personalization from CSV data
                      </span>
                      <span
                        className={
                          smsTemplate.length > 160
                            ? 'font-medium text-yellow-600'
                            : ''
                        }
                      >
                        {smsTemplate.length}/160{' '}
                        {smsTemplate.length > 160 && '(will be split)'}
                      </span>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Advanced Settings (Collapsible) */}
            <div className="space-y-4">
              <button
                type="button"
                className="flex items-center gap-2 text-sm text-muted-foreground transition-colors hover:text-foreground"
                onClick={() => setShowAdvanced(!showAdvanced)}
              >
                {showAdvanced ? (
                  <ChevronUp className="h-4 w-4" />
                ) : (
                  <ChevronDown className="h-4 w-4" />
                )}
                Advanced Settings
                {!showAdvanced && (
                  <span className="text-xs">
                    (Country: {countryCode}, Hours: {startHour}:00-{endHour}:00,{' '}
                    {allowedDays.length} days)
                  </span>
                )}
              </button>

              {showAdvanced && (
                <div className="space-y-4 border-l-2 border-muted pl-6">
                  {/* Country Code */}
                  <div className="space-y-2">
                    <Label htmlFor="countryCode">Default Country Code</Label>
                    <Select value={countryCode} onValueChange={setCountryCode}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {countryCodes.map((cc) => (
                          <SelectItem key={cc.code} value={cc.code}>
                            {cc.code} ({cc.country})
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <p className="text-sm text-muted-foreground">
                      Applied to local phone numbers without country code
                    </p>
                  </div>

                  {/* Time Window */}
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="startHour">Start Hour</Label>
                      <Select
                        value={startHour.toString()}
                        onValueChange={(v) => setStartHour(parseInt(v))}
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {Array.from({ length: 24 }, (_, i) => (
                            <SelectItem key={i} value={i.toString()}>
                              {i.toString().padStart(2, '0')}:00
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="endHour">End Hour</Label>
                      <Select
                        value={endHour.toString()}
                        onValueChange={(v) => setEndHour(parseInt(v))}
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {Array.from({ length: 24 }, (_, i) => (
                            <SelectItem key={i} value={i.toString()}>
                              {i.toString().padStart(2, '0')}:00
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  {/* Active Days */}
                  <div className="space-y-2">
                    <Label>Active Days</Label>
                    <div className="flex flex-wrap gap-3">
                      {[
                        'sunday',
                        'monday',
                        'tuesday',
                        'wednesday',
                        'thursday',
                        'friday',
                        'saturday',
                      ].map((day) => (
                        <label
                          key={day}
                          className="flex cursor-pointer items-center gap-1.5"
                        >
                          <Checkbox
                            checked={allowedDays.includes(day)}
                            onCheckedChange={(checked) => {
                              if (checked) {
                                setAllowedDays([...allowedDays, day]);
                              } else {
                                setAllowedDays(
                                  allowedDays.filter((d) => d !== day),
                                );
                              }
                            }}
                          />
                          <span className="text-sm capitalize">
                            {day.slice(0, 3)}
                          </span>
                        </label>
                      ))}
                    </div>
                  </div>

                  {/* Survey Response Extraction (voice only) */}
                  {channel === 'voice' && (
                    <div className="space-y-2">
                      <div className="flex items-center space-x-2">
                        <Checkbox
                          id="enableSurvey"
                          checked={enableSurvey}
                          onCheckedChange={(checked) =>
                            setEnableSurvey(checked === true)
                          }
                        />
                        <Label
                          htmlFor="enableSurvey"
                          className="cursor-pointer"
                        >
                          Extract survey response
                        </Label>
                      </div>
                      <p className="pl-6 text-sm text-muted-foreground">
                        Enables post-call extraction of yes/no/maybe responses
                        for analytics.
                      </p>
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Template Settings */}
            {selectedTemplate && (
              <div className="flex items-center justify-between rounded-lg bg-muted/50 px-4 py-3">
                <p className="text-sm text-muted-foreground">
                  Save current settings for future {selectedTemplate.name}{' '}
                  campaigns
                </p>
                <div className="flex gap-2">
                  {selectedTemplate.is_customized && (
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={handleResetSettings}
                      disabled={resettingSettings}
                    >
                      <RotateCcw
                        className={`mr-1 h-4 w-4 ${resettingSettings ? 'animate-spin' : ''}`}
                      />
                      {resettingSettings ? 'Resetting...' : 'Reset'}
                    </Button>
                  )}
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={handleSaveSettings}
                    disabled={savingSettings}
                  >
                    <Save className="mr-1 h-4 w-4" />
                    {savingSettings ? 'Saving...' : 'Save for Future'}
                  </Button>
                </div>
              </div>
            )}

            {/* Contact Entry */}
            <div className="space-y-4 border-t pt-4">
              <div className="flex items-center justify-between">
                <Label>Contacts *</Label>
                <div className="flex gap-2">
                  <Button
                    type="button"
                    variant={inputMode === 'manual' ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => setInputMode('manual')}
                  >
                    <Users className="mr-2 h-4 w-4" />
                    Manual Entry
                  </Button>
                  <Button
                    type="button"
                    variant={inputMode === 'csv' ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => setInputMode('csv')}
                  >
                    <Upload className="mr-2 h-4 w-4" />
                    CSV Upload
                  </Button>
                </div>
              </div>

              {inputMode === 'manual' ? (
                /* Manual Entry Form */
                <div className="space-y-3">
                  {!selectedTemplate ? (
                    <p className="py-4 text-center text-sm text-muted-foreground">
                      Select a campaign type first to see required fields
                    </p>
                  ) : (
                    <>
                      <p className="text-sm text-muted-foreground">
                        Required fields:{' '}
                        {selectedTemplate.required_columns.join(', ')}
                      </p>
                      {manualContacts.map((contact, index) => (
                        <div
                          key={index}
                          className="flex flex-wrap items-start gap-2"
                        >
                          {selectedTemplate.required_columns.map((column) => (
                            <div key={column} className="min-w-[120px] flex-1">
                              <Input
                                placeholder={`${formatColumnName(column)}${column === 'phone' ? ' *' : ''}`}
                                value={contact[column] || ''}
                                onChange={(e) =>
                                  updateContact(index, column, e.target.value)
                                }
                              />
                            </div>
                          ))}
                          {manualContacts.length > 1 && (
                            <Button
                              type="button"
                              variant="ghost"
                              size="icon"
                              onClick={() => removeContact(index)}
                            >
                              <X className="h-4 w-4" />
                            </Button>
                          )}
                        </div>
                      ))}
                      <div className="flex items-center justify-between">
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={addContact}
                        >
                          <Plus className="mr-2 h-4 w-4" />
                          Add Another
                        </Button>
                        {validManualContacts.length > 0 && (
                          <p className="text-sm text-muted-foreground">
                            {validManualContacts.length} contact
                            {validManualContacts.length !== 1 ? 's' : ''}
                          </p>
                        )}
                      </div>
                    </>
                  )}
                </div>
              ) : (
                /* CSV Upload */
                <div className="space-y-4">
                  {selectedTemplate && (
                    <div className="flex items-center justify-between">
                      <p className="text-sm text-muted-foreground">
                        Required columns:{' '}
                        {selectedTemplate.required_columns.join(', ')}
                      </p>
                      {templateId && (
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={handleDownloadTemplate}
                        >
                          <Download className="mr-2 h-4 w-4" />
                          Download Template
                        </Button>
                      )}
                    </div>
                  )}
                  <div
                    className={`cursor-pointer rounded-lg border-2 border-dashed p-8 text-center transition-colors ${
                      csvFile
                        ? 'border-green-500 bg-green-50'
                        : 'border-muted-foreground/25 hover:border-muted-foreground/50'
                    }`}
                    onClick={() => fileInputRef.current?.click()}
                  >
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept=".csv"
                      className="hidden"
                      onChange={handleFileChange}
                    />
                    {csvFile ? (
                      <div className="flex flex-col items-center gap-2">
                        <FileSpreadsheet className="h-10 w-10 text-green-600" />
                        <p className="font-medium">{csvFile.name}</p>
                        <p className="text-sm text-muted-foreground">
                          Click to change file
                        </p>
                      </div>
                    ) : (
                      <div className="flex flex-col items-center gap-2">
                        <Upload className="h-10 w-10 text-muted-foreground" />
                        <p className="font-medium">Click to upload CSV</p>
                        <p className="text-sm text-muted-foreground">
                          or drag and drop
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>

            {/* Error/Success Messages */}
            {error && (
              <div className="flex items-center gap-2 text-destructive">
                <AlertCircle className="h-4 w-4" />
                <p className="text-sm">{error}</p>
              </div>
            )}
            {success && (
              <div className="flex items-center gap-2 text-green-600">
                <CheckCircle className="h-4 w-4" />
                <p className="text-sm">{success}</p>
              </div>
            )}

            {/* Submit */}
            <div className="flex justify-end gap-2 pt-4">
              <Button type="button" variant="outline" asChild>
                <Link to="/campaigns">Cancel</Link>
              </Button>
              <Button type="submit" disabled={submitting}>
                {submitting ? 'Creating...' : 'Create Campaign'}
              </Button>
            </div>
          </CardContent>
        </Card>
      </form>
    </div>
  );
}
